import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useAuth } from './context/AuthContext';
import Landing from './pages/Landing';
import Login from './pages/auth/Login';
import ActivateAccount from './pages/auth/ActivateAccount';
import ResetPassword from './pages/auth/ResetPassword';
import MainLayout from './layouts/MainLayout';
import ClientManager from './pages/admin/ClientManager';
import CompanyManager from './pages/admin/CompanyManager';
import TemplateManager from './pages/admin/TemplateManager';
import TemplateDetail from './pages/admin/TemplateDetail';
import LlmSettings from './pages/admin/LlmSettings';
import GeneralSettingsPage from './pages/admin/GeneralSettingsPage';
import AnnexeSettingsPage from './pages/admin/AnnexeSettingsPage';
import ProjectTypeSettings from './pages/admin/ProjectTypeSettings';
import AiTemplateBuilder from './pages/admin/AiTemplateBuilder';
import DocumentsPage from './pages/admin/DocumentsPage';
import EchangesPage from './pages/admin/EchangesPage';
import SubmissionList from './pages/portal/SubmissionList';
import NewSubmission from './pages/portal/NewSubmission';
import SubmissionDetail from './pages/portal/SubmissionDetail';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';

function App() {
  const { user, loading } = useAuth();

  if (loading) return <div>Chargement...</div>;

  // Helper pour vérifier les rôles
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
  const isDev = user?.role === 'DEV' || user?.role === 'TECHNICIEN';
  const isClientAdmin = user?.role === 'CLIENTADMIN';
  const isInternalRole = isAdmin || isDev;

  return (
    <BrowserRouter>
      {!user ? (
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/activate" element={<ActivateAccount />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      ) : (
        <MainLayout>
          <Routes>
            {/* Routes pour ADMIN et SUPERADMIN - accès complet */}
            {isAdmin && (
              <>
                <Route path="/admin" element={<Dashboard />} />
                <Route path="/admin/clients" element={<ClientManager />} />
                <Route path="/admin/companies" element={<CompanyManager />} />
                <Route path="/admin/submissions" element={<SubmissionList type="devis" />} />
                <Route path="/admin/submissions/new" element={<NewSubmission />} />
                <Route path="/admin/submissions/:id" element={<SubmissionDetail />} />
                <Route path="/admin/submissions/:id/documents" element={<DocumentsPage />} />
                <Route path="/admin/submissions/:id/echanges" element={<EchangesPage />} />
                <Route path="/admin/projects" element={<SubmissionList type="project" />} />
                <Route path="/admin/templates" element={<TemplateManager />} />
                <Route path="/admin/templates/ai/new" element={<AiTemplateBuilder />} />
                <Route path="/admin/templates/:id" element={<TemplateDetail />} />
                <Route path="/admin/project-types" element={<ProjectTypeSettings />} />
                <Route path="/admin/settings/general" element={<GeneralSettingsPage />} />
                <Route path="/admin/settings/annexe" element={<AnnexeSettingsPage />} />
                {isSuperAdmin && <Route path="/admin/llm" element={<LlmSettings />} />}
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </>
            )}

            {/* Routes pour DEV - Dashboard, Devis, Projets + Paramètres complets */}
            {user?.role === 'DEV' && (
              <>
                <Route path="/admin" element={<Dashboard />} />
                <Route path="/admin/submissions" element={<SubmissionList type="devis" />} />
                <Route path="/admin/submissions/:id" element={<SubmissionDetail />} />
                <Route path="/admin/submissions/:id/documents" element={<DocumentsPage />} />
                <Route path="/admin/submissions/:id/echanges" element={<EchangesPage />} />
                <Route path="/admin/projects" element={<SubmissionList type="project" />} />
                <Route path="/admin/project-types" element={<ProjectTypeSettings />} />
                <Route path="/admin/settings/general" element={<GeneralSettingsPage />} />
                <Route path="/admin/settings/annexe" element={<AnnexeSettingsPage />} />
                <Route path="/admin/llm" element={<LlmSettings />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </>
            )}

            {/* Routes pour TECHNICIEN - Dashboard, Devis, Projets + Mon compte seulement */}
            {user?.role === 'TECHNICIEN' && (
              <>
                <Route path="/admin" element={<Dashboard />} />
                <Route path="/admin/submissions" element={<SubmissionList type="devis" />} />
                <Route path="/admin/submissions/:id" element={<SubmissionDetail />} />
                <Route path="/admin/submissions/:id/documents" element={<DocumentsPage />} />
                <Route path="/admin/submissions/:id/echanges" element={<EchangesPage />} />
                <Route path="/admin/projects" element={<SubmissionList type="project" />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </>
            )}

            {/* Routes pour CLIENTADMIN - accès client + gestion utilisateurs de son entreprise */}
            {isClientAdmin && (
              <>
                <Route path="/portal" element={<Dashboard />} />
                <Route path="/portal/submissions" element={<SubmissionList type="devis" />} />
                <Route path="/portal/submissions/new" element={<NewSubmission />} />
                <Route path="/portal/submissions/:id" element={<SubmissionDetail />} />
                <Route path="/portal/submissions/:id/documents" element={<DocumentsPage />} />
                <Route path="/portal/submissions/:id/echanges" element={<EchangesPage />} />
                <Route path="/portal/projects" element={<SubmissionList type="project" />} />
                <Route path="/portal/users" element={<ClientManager />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/portal" replace />} />
              </>
            )}

            {/* Routes pour CLIENT standard */}
            {user?.role === 'CLIENT' && (
              <>
                <Route path="/portal" element={<Dashboard />} />
                <Route path="/portal/submissions" element={<SubmissionList type="devis" />} />
                <Route path="/portal/submissions/new" element={<NewSubmission />} />
                <Route path="/portal/submissions/:id" element={<SubmissionDetail />} />
                <Route path="/portal/submissions/:id/documents" element={<DocumentsPage />} />
                <Route path="/portal/submissions/:id/echanges" element={<EchangesPage />} />
                <Route path="/portal/projects" element={<SubmissionList type="project" />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/portal" replace />} />
              </>
            )}
          </Routes>
        </MainLayout>
      )}
    </BrowserRouter>
  );
}

export default App;
